// ./src/form/NumberField.tsx
//
// NumberInput wrapper on the shared `(name, value)` change contract. Emits a
// number, or null when the field is cleared/empty.
//
// ⚠⚠ MANTINE HANDS BACK A STRING WHILE A NUMBER IS HALF TYPED. "13." (a trailing decimal point), "13.70" (a trailing
// zero), "0.0" and "-" all arrive as text, not as a number. Mapping every string to null — as this did until
// 2026-10-02 — emptied the field the instant the decimal point was pressed, so NO decimal could be typed anywhere in
// the app (a 13.75% tax rate, a 0.5 hour, a 12.50 price); only pasting worked. So what is typed is kept on screen while
// it still means the value the form holds, and the form is told the number it means ("13." → 13, "-" → null).
import React, { useEffect, useState } from 'react';
import { NumberInput, NumberInputProps } from '@mantine/core';

import { FieldChangeHandler } from './types';

export interface NumberFieldProps extends Omit<NumberInputProps, 'onChange' | 'value'> {
  name?: string;
  value?: number | null;
  onChange?: FieldChangeHandler<number | null>;
}

/** The number a typed value means: a number as is; text parsed ("13." → 13); empty, "-" or nonsense → null. */
export const numberFieldValue = (typed: number | string | null | undefined): number | null => {
  if (typeof typed === 'number') {
    return Number.isFinite(typed) ? typed : null;
  }

  const text = String(typed ?? '').trim();

  if (text === '' || text === '-' || text === '.' || text === '-.') {
    return null;
  }

  const n = Number(text);

  return Number.isFinite(n) ? n : null;
};

export const NumberField: React.FC<NumberFieldProps> = ({ name, value, onChange, ...rest }) => {
  const [typed, setTyped] = useState<number | string>(value ?? '');

  // A value set from outside (a reset, a reload, another control) replaces what is typed; the echo of our own change
  // does not — "13." means 13, so the form's 13 must not turn it back into "13" and eat the decimal point.
  useEffect(() => {
    setTyped((prev) => (numberFieldValue(prev) === (value ?? null) ? prev : value ?? ''));
  }, [value]);

  return (
    <NumberInput
      value={typed}
      onChange={(v) => {
        setTyped(v);
        onChange?.(name, numberFieldValue(v));
      }}
      {...rest}
    />
  );
};
